/**
 * @module Reducers.SubscriptionPlan
 *
 */
import { RequestError } from 'typedefs';
import { SubscriptionPlanModel } from 'app/models';
import { SubscriptionPlanAction } from 'app/actions/subscription-plan';

export type SubscriptionPlanState = {
  data: SubscriptionPlanModel[],
  // The currency `data` is priced in; a consumer asking for another one must
  // not read these prices
  currency: string | null,
  // The currency of the latest request, so a slower earlier response cannot
  // overwrite it
  requested: string | null,
  error: RequestError | null,
  pending: boolean,
};

const initialState: SubscriptionPlanState = {
  data: null,
  currency: null,
  requested: null,
  error: null,
  pending: false,
};

export default function subscriptionPlanReducer(state = initialState, action: SubscriptionPlanAction): SubscriptionPlanState {
  switch (action.type) {
    case '@subscription-plan.fetch.pending':
      // The prices on hand are stale until the new request answers: a failed
      // refresh must not re-enable them
      return { ...state, requested: action.payload, currency: null, pending: true, error: null };
    case '@subscription-plan.fetch.success':
      if (action.payload.currency !== state.requested) {
        return state;
      }
      return { ...state, pending: false, data: action.payload.plans, currency: action.payload.currency };
    case '@subscription-plan.fetch.error':
      // A failure of a superseded request says nothing about the one still in
      // flight: only the current currency's failure is shown.
      if (action.payload.currency !== state.requested) {
        return state;
      }
      return { ...state, pending: false, error: action.payload.error };

    default:
      return state;
  }
}
